import React from 'react'
import { Global, css, useTheme, SerializedStyles } from '@emotion/react'

import reset from './reset'
import defaultTypography from './typography'
import layoutStyles from './layout'
import { COLORS } from './constants'

interface GlobalStylesProps {
  typography?: SerializedStyles
}

export const GlobalStyles: React.FC<GlobalStylesProps> = ({ typography = defaultTypography }) => {
  const theme = useTheme()

  return (
    <Global
      styles={css`

        ${reset}
        ${layoutStyles}
        ${typography}
        
        html {   
          font-family:  ${theme.font.name}, sans-serif;       
          --transition: ${theme.transition};
          --zindex-modal: 301;
          --zindex-modal--backdrop: 300;
          --zindex-dropdown: 200;
          --zindex-toast: 400;

          /* Colors globals */
          --colors-background: ${theme.brand.background};
          --colors-foreground: ${theme.brand.foreground};
          --colors-modal--backdrop: ${theme.brand.modalBackdrop};
          --colors-hairline: ${theme.brand.hairline};
          --colors-highlight: ${theme.brand.highlight};
          --colors-neutral: ${theme.brand.neutral};
          --colors-subtle: ${theme.brand.subtle};
          --colors-light: ${theme.brand.light};
          --colors-action: ${theme.brand.action};
          --colors-action--subtle: ${theme.brand.actionSubtle};
          --colors-action--light: ${theme.brand.actionLight};
          --colors-info: ${theme.brand.info};
          --colors-info--subtle: ${theme.brand.infoSubtle};
          --colors-info--light: ${theme.brand.infoLight};
          --colors-positive: ${theme.brand.positive};
          --colors-positive--subtle: ${theme.brand.positiveSubtle};
          --colors-negative: ${theme.brand.negative};
          --colors-negative--subtle: ${theme.brand.negativeSubtle};
          --colors-caution: ${theme.brand.caution};
          --colors-caution--subtle: ${theme.brand.cautionSubtle};
          --colors-caution--dark: ${theme.brand.cautionDark};
          --colors-contrast: ${theme.brand.contrast};
          --colors-ghost: ${COLORS.ghost};
          --colors-icon-base: ${theme.brand.iconBase};
          --colors-icon-accent: ${theme.brand.iconAccent};

          /* Button theme styles */
          --button-bg--primary: ${theme.button.bg.primary};
          --button-text--primary: ${theme.button.text.primary};
          --button-border--primary: ${theme.button.border.primary};
          --button-hover--primary: ${theme.button.hover.primary};
          
          --button-bg--secondary: ${theme.button.bg.secondary};
          --button-text--secondary: ${theme.button.text.secondary};
          --button-border--secondary: ${theme.button.border.secondary};
          --button-hover--secondary: ${theme.button.hover.secondary};

          --button-bg--destructive: ${theme.button.bg.destructive};
          --button-text--destructive ${theme.button.text.destructive};
          --button-border--destructive: ${theme.button.border.destructive};
          --button-hover--destructive: ${theme.button.hover.destructive};

          --button-text--text: ${theme.button.text.link};
          --button-border-text: ${theme.button.border.link};
          --button-hover--text: ${theme.button.hover.link};

          /* Card theme styles */
          --card--background: ${theme.card.bg};
          --card--hairline: ${theme.card.hairline};
          --card--border: ${theme.card.border};

          /* Choice Chip theme styles */
          --choice-chip-text--default: ${theme['choice-chip'].text.default};
          --choice-chip-hover--default: ${theme['choice-chip'].hover.default};
          --choice-chip-bg--default: ${theme['choice-chip'].bg.default};
          --choice-chip-border--default: ${theme['choice-chip'].border.default};

          --choice-chip-text--active: ${theme['choice-chip'].text.active};
          --choice-chip-bg--active: ${theme['choice-chip'].bg.active};
          --choice-chip-border--active: ${theme['choice-chip'].border.active};

          --choice-chip-text--disabled: ${theme['choice-chip'].text.disabled};
          --choice-chip-bg--disabled: ${theme['choice-chip'].bg.disabled};
          --choice-chip-border--disabled: ${theme['choice-chip'].border.disabled};

          /* Input Chip theme styles */
          --input-chip-bg--neutral: ${theme['input-chip'].bg.neutral};
          --input-chip-bg--negative: ${theme['input-chip'].bg.negative};
          --input-chip-bg--positive: ${theme['input-chip'].bg.positive};
        
          /* Text Field theme styles */
          --textfield-bg--disabled: ${theme.textfield.bg.disabled};
          --textfield-border--neutral: ${theme.textfield.border.neutral};
          --textfield-border--negative: ${theme.textfield.border.negative};
      `}
    />
  )
}
